import React, {
  useState,
  Fragment,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMDialog } from "../../../utils/PMDialog";
import { PMButton } from "../../../utils/PMButton";
import { dispatchWithToastPromise, stripHtml } from "../../../Utils";
import { updateReportAsync } from "../../../../slices/service/reports";
import { useDispatch } from "react-redux";
import ReportItemConsumptionList from "./report-item-consumption-list";
import {
  PromangRichTextEditor,
  DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU,
} from "../../../utils/PromangRichTextEditor";
import { ReportVariables } from "./report-variables";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { generateReportFormatting } from "../../../../api/service/report";
import { ReportChecklistDialog } from "./report-checklist-dialog";
import { SaveBar } from "../../../utils/SaveBar";
import toast from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
// Module-level variable to persist across renders
let isSubmitting = false;

/** After open, Quill normalizes HTML and fires onChange; sync baseline without autosaving. */
const AUTOSAVE_EDITOR_BASELINE_MS = 800;

function ReportSectionReportBody({
  formik,
  report,
  canEdit,
  open,
  dispatch,
  onRefresh,
  reportLanguage,
  setReportLanguage,
  isGeneratingFormatting,
  handleGenerateReportFormatting,
  flushRef,
  onSaveUiChange,
}) {
  const { t } = useTranslation();
  const debounceTimerRef = useRef(null);
  const saveGenerationRef = useRef(0);
  const lastPersistedRef = useRef({ report: "", internal_report: "" });
  const formikValuesRef = useRef({ report: "", internal_report: "" });
  const reportSnapshotRef = useRef(report);
  const savedUiResetRef = useRef(null);
  const autosaveBaselineUntilRef = useRef(0);
  const [saveUi, setSaveUi] = useState("idle");

  reportSnapshotRef.current = report;
  formikValuesRef.current = {
    report: formik.values.report ?? "",
    internal_report: formik.values.internal_report ?? "",
  };

  useEffect(() => {
    if (open && report) {
      lastPersistedRef.current = {
        report: report.report ?? "",
        internal_report: report.internal_report ?? "",
      };
      autosaveBaselineUntilRef.current = Date.now() + AUTOSAVE_EDITOR_BASELINE_MS;
    } else if (!open) {
      autosaveBaselineUntilRef.current = 0;
    }
  }, [open, report?.id, report?.updated_at]);

  const saveNow = useCallback(
    async (r, i) => {
      if (!canEdit || !reportSnapshotRef.current?.id) return;
      const lr = lastPersistedRef.current.report ?? "";
      const li = lastPersistedRef.current.internal_report ?? "";
      if (r === lr && i === li) return;

      const generation = ++saveGenerationRef.current;
      setSaveUi("saving");
      try {
        const updatedReport = await dispatch(
          updateReportAsync({
            id: reportSnapshotRef.current.id,
            report: r,
            internal_report: i,
          })
        ).unwrap();

        if (generation !== saveGenerationRef.current) {
          return;
        }

        const base = reportSnapshotRef.current;
        // User may keep typing while the request is in flight. Pushing only the
        // saved snapshot into parent + Formik enableReinitialize would reset the
        // editor to stale HTML; always publish the latest draft for body fields.
        const latestDraft = formikValuesRef.current;
        const mergedReport = {
          ...base,
          ...updatedReport,
          report: latestDraft.report ?? r,
          internal_report: latestDraft.internal_report ?? i,
          variables: updatedReport?.variables ?? base?.variables,
          items: updatedReport?.items ?? base?.items,
          checklist: updatedReport?.checklist ?? base?.checklist,
          operations: updatedReport?.operations ?? base?.operations,
          updated_at: updatedReport?.updated_at ?? base?.updated_at,
        };
        lastPersistedRef.current = { report: r, internal_report: i };
        onRefresh(mergedReport);
        setSaveUi("saved");
        if (savedUiResetRef.current) window.clearTimeout(savedUiResetRef.current);
        savedUiResetRef.current = window.setTimeout(() => setSaveUi("idle"), 2000);
      } catch (e) {
        if (generation !== saveGenerationRef.current) {
          return;
        }
        console.error(e);
        setSaveUi("error");
        toast.error(e?.message || t("report.sectionDialog.autoSaveError"));
        throw e;
      }
    },
    [canEdit, dispatch, onRefresh, t]
  );

  useEffect(() => {
    if (!open || !canEdit) return;
    const { report: r, internal_report: i } = formikValuesRef.current;
    const lr = lastPersistedRef.current.report ?? "";
    const li = lastPersistedRef.current.internal_report ?? "";
    if (r === lr && i === li) return;

    // During baseline window, follow editor/Quill output without scheduling autosave.
    if (
      autosaveBaselineUntilRef.current > 0 &&
      Date.now() < autosaveBaselineUntilRef.current
    ) {
      lastPersistedRef.current = { report: r, internal_report: i };
      window.clearTimeout(debounceTimerRef.current);
      return;
    }

    window.clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = window.setTimeout(() => {
      const fr = formikValuesRef.current.report ?? "";
      const fi = formikValuesRef.current.internal_report ?? "";
      void saveNow(fr, fi);
    }, 1800);

    return () => window.clearTimeout(debounceTimerRef.current);
  }, [formik.values.report, formik.values.internal_report, open, canEdit, saveNow]);

  useLayoutEffect(() => {
    flushRef.current = async () => {
      window.clearTimeout(debounceTimerRef.current);
      const fr = formikValuesRef.current.report ?? "";
      const fi = formikValuesRef.current.internal_report ?? "";
      await saveNow(fr, fi);
    };
    return () => {
      flushRef.current = null;
    };
  }, [flushRef, saveNow]);

  useEffect(
    () => () => {
      window.clearTimeout(debounceTimerRef.current);
      if (savedUiResetRef.current) window.clearTimeout(savedUiResetRef.current);
    },
    []
  );

  useEffect(() => {
    onSaveUiChange?.(saveUi);
  }, [saveUi, onSaveUiChange]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">{t("report.sectionDialog.autoSaveHint")}</p>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            {t("report.sectionDialog.reportContentLabel")}
          </label>
          <div className="flex items-center gap-2">
            <select
              value={reportLanguage}
              onChange={(e) => setReportLanguage(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isGeneratingFormatting || !canEdit}
            >
              <option value="no">Norsk</option>
              <option value="en">English</option>
              <option value="sv">Svenska</option>
              <option value="da">Dansk</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
            </select>
            <button
              type="button"
              onClick={() => handleGenerateReportFormatting(formik)}
              disabled={isGeneratingFormatting || !canEdit}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {isGeneratingFormatting
                ? t("report.sectionDialog.formatting")
                : t("report.sectionDialog.formatWithAi")}
            </button>
          </div>
        </div>
        <PromangRichTextEditor
          key={`report-body-${report?.id}`}
          value={formik.values.report ?? ""}
          onChange={(html) => formik.setFieldValue("report", html)}
          placeholder={t("report.sectionDialog.placeholderReport")}
          disabled={!canEdit}
          size="lg"
          framed={false}
          contextMenu={DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("report.sectionDialog.internalReportLabel")}
        </label>
        <PromangRichTextEditor
          key={`internal-report-body-${report?.id}`}
          value={formik.values.internal_report ?? ""}
          onChange={(html) => formik.setFieldValue("internal_report", html)}
          placeholder={t("report.sectionDialog.placeholderInternal")}
          disabled={!canEdit}
          size="lg"
          framed={false}
          contextMenu={DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU}
        />
      </div>
    </div>
  );
}

const ReportSectionDialogComponent = ({
  open,
  onClose,
  report,
  section,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const canManage = useUserHasAccess({ role: "report", action: "manage" });
  const hasServiceReportAdminAccess = useUserHasAccess({
    role: "service",
    action: "reset",
  });
  const [compactMode, setCompactMode] = useState(true);
  const [isDialogLoading, setIsDialogLoading] = useState(true);
  const [isGeneratingFormatting, setIsGeneratingFormatting] = useState(false);
  const [reportLanguage, setReportLanguage] = useState("no"); // Default to Norwegian
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [hasOpenSubDialog, setHasOpenSubDialog] = useState(false); // Track if any sub-dialog is open
  const reportTextFlushRef = useRef(null);
  const [reportSaveUi, setReportSaveUi] = useState("idle");
  const handleReportSaveUiChange = useCallback((ui) => {
    setReportSaveUi(ui);
  }, []);

  // Open checklist dialog automatically when section is "checklist"
  useEffect(() => {
    if (open && section === "checklist") {
      setChecklistDialogOpen(true);
    } else {
      setChecklistDialogOpen(false);
    }
  }, [open, section]);

  useEffect(() => {
    if (!open || section !== "report") setReportSaveUi("idle");
  }, [open, section]);

  const canEdit =
    hasServiceReportAdminAccess ||
    report?.user_can_edit_with_rights !== false ||
    report?.user_can_edit !== false;

  const closeWithFlush = async () => {
    if (hasOpenSubDialog) return;
    if (section === "report" && reportSaveUi === "saving") return;
    if (section === "report" && reportTextFlushRef.current) {
      try {
        await reportTextFlushRef.current();
      } catch {
        return;
      }
    }
    onClose();
  };

  // Live update report object when dialog opens
  useEffect(() => {

    if (open && report?.id) {
      // Set loading to false after a short delay to ensure report data is fully loaded
      const timer = setTimeout(() => {
        setIsDialogLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsDialogLoading(true);
    }
  }, [open, report?.id]);

  const handleGenerateReportFormatting = async (formik) => {
    // Validate before generating
    const reportText = formik.values.report || report?.report || "";
    const plainText = stripHtml(reportText);
    
    if (!reportText || plainText.trim().length < 10) {
      setValidationMessage(
        "Rapporten må ha minst 10 tegn med innhold for å generere formatering. " +
        "Vennligst legg til mer informasjon i rapporten først."
      );
      setShowValidationDialog(true);
      return;
    }

    setIsGeneratingFormatting(true);
    try {
      const response = await generateReportFormatting({
        id: report?.id,
        report: { report: reportText, language: reportLanguage },
      });

      if (response?.data?.formatted_report) {
        formik.setFieldValue("report", response.data.formatted_report);
        toast.success("Rapport formatert");
      } else if (response?.data?.errors) {
        setValidationMessage(
          response.data.errors.join(", ") || "Kunne ikke formatere rapport"
        );
        setShowValidationDialog(true);
      } else {
        toast.error("Kunne ikke formatere rapport");
      }
    } catch (error) {
      console.error("Error generating report formatting:", error);
      const errorMessage = error?.response?.data?.errors?.join(", ") || 
                          error?.response?.data?.error ||
                          "Kunne ikke formatere rapport. Prøv igjen senere.";
      setValidationMessage(errorMessage);
      setShowValidationDialog(true);
    } finally {
      setIsGeneratingFormatting(false);
    }
  };

  const getSectionConfig = () => {
    switch (section) {
      case "items":
        return {
          title: "Vareforbruk",
          description: "Registrer forbruk av materialer og varer",
          field: "items",
          component: null, // ReportItemConsumptionList is rendered directly in renderSectionContent
          validation: Yup.object({
            items: Yup.array().of(
              Yup.object({
                product_id: Yup.number().required("Produkt må velges"),
                quantity: Yup.number().required("Antall må angis").min(0.01, "Antall må være større enn 0"),
              })
            ),
          }),
        };
      case "variables":
        return {
          title: "Variabler",
          description: "Registrer variabler for rapporten",
          field: "variables",
          component: null, // ReportVariables component
          validation: Yup.object({}),
        };
      case "report":
        return {
          title: t("report.sectionDialog.reportTitle"),
          description: t("report.sectionDialog.reportDescription"),
          field: "report",
          component: null,
          validation: Yup.object({}),
        };
      case "checklist":
        return {
          title: "Sjekkliste",
          description: "Fullfør sjekkliste for rapporten",
          field: "checklist",
          component: null, // ReportChecklistDialog component
          validation: Yup.object({}),
        };
      default:
        return null;
    }
  };

  const config = getSectionConfig();
  if (!config) return null;

  const getInitialValues = () => {


    const base = { id: report?.id };

    switch (section) {
      case "items": {
        const itemsValues = {
          ...base,
          items: (report?.items || []).filter((item) =>
            canManage ? true : !item.is_quote_line
          ),
        };
        return itemsValues;
      }
      case "variables": {
        const variablesValues = { ...base, variables: report?.variables };

        return variablesValues;
      }
      case "report": {
        const reportValues = {
          ...base,
          report: report?.report ?? "",
          internal_report: report?.internal_report ?? "",
        };

        return reportValues;
      }
      case "checklist": {
        // Checklist is now loaded separately, so we don't need initial values
        return base;
      }
      default:
        return base;
    }
  };



  // const handleSubmit = async (values, helpers) => {
  //   // Check if user can edit this report (use the admin-override version if available)
  //   if (
  //     !(
  //       report?.user_can_edit_with_rights !== false ||
  //       report?.user_can_edit !== false
  //     )
  //   ) {
  //     toast.error("Du kan ikke redigere denne rapporten");
  //     return;
  //   }

  //   // Prevent multiple submissions
  //   if (isLoading) {
  //     console.log("Already submitting, ignoring duplicate submission");
  //     return;
  //   }

  //   setIsLoading(true);

  //   try {
  //     const result = await dispatchWithToast(
  //       dispatch(updateReportAsync(values)),
  //       (updatedReport) => {

  //         // Create a merged report object with the updated values
  //         const mergedReport = {
  //           ...report,
  //           ...updatedReport,
  //           // Ensure specific fields are properly merged
  //           variables:
  //             values.variables || updatedReport?.variables || report?.variables,
  //           items: values.items || updatedReport?.items || report?.items,
  //           checklist:
  //             values.checklist || updatedReport?.checklist || report?.checklist,
  //           report:
  //             values.report !== undefined
  //               ? values.report
  //               : updatedReport?.report || report?.report,
  //           internal_report:
  //             values.internal_report !== undefined
  //               ? values.internal_report
  //               : updatedReport?.internal_report || report?.internal_report,
  //           operations: updatedReport?.operations || report?.operations,
  //           updated_at: updatedReport?.updated_at || report?.updated_at,
  //         };

  //         // Pass the merged report data directly to avoid server refresh
  //         onRefresh(mergedReport);
  //         onClose();
  //       }
  //     );

  //     helpers.setStatus({ success: true });
  //     console.log("Submit completed successfully");
  //   } catch (err) {
  //     console.error("Submit error:", err);
  //     helpers.setStatus({ success: false });
  //     helpers.setErrors({ submit: err.message });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };


  const renderSectionContent = (formik) => {
    switch (section) {
      case "items":
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900"></h3>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Kompakt visning</label>
                <input
                  type="checkbox"
                  checked={compactMode}
                  onChange={(e) => setCompactMode(e.target.checked)}
                  disabled={!canEdit}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
              <ReportItemConsumptionList
                items={formik.values.items || []}
                onItemsChange={(updatedItems) => {
                  // Update formik values for consistency, but items are saved directly
                  formik.setFieldValue("items", updatedItems);
                }}
                disabled={!canEdit}
                report={report}
                onSaveItem={undefined}
                onSubDialogStateChange={setHasOpenSubDialog}
              />
          </div>
        );
      case "variables":
        return (
          <div className="grid grid-cols-6 gap-6 mt-10">
            <div className="col-span-6 sm:col-span-6">
              <ReportVariables
                key={`variables-${report?.id}-${report?.updated_at}`}
                disabled={!canEdit}
                {...formik}
              />
            </div>
          </div>
        );
      case "report":
        return (
          <ReportSectionReportBody
            formik={formik}
            report={report}
            canEdit={canEdit}
            open={open}
            dispatch={dispatch}
            onRefresh={onRefresh}
            reportLanguage={reportLanguage}
            setReportLanguage={setReportLanguage}
            isGeneratingFormatting={isGeneratingFormatting}
            handleGenerateReportFormatting={handleGenerateReportFormatting}
            flushRef={reportTextFlushRef}
            onSaveUiChange={handleReportSaveUiChange}
          />
        );
      case "checklist":
        // Checklist is now handled by a separate dialog that opens automatically
        // This section doesn't render anything - the dialog opens via useEffect
        return null;
      default:
        return null;
    }
  };

  return (
    <Fragment>
      <PMDialog
        title={config.title}
        open={open}
        onClose={() => {
          void closeWithFlush();
        }}
        size="5xl"
        maxWidth="7xl"
        closeOnOutsideClick={!hasOpenSubDialog}
      >
        <div className="space-y-6">
          {!canEdit && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Kun visning
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      Denne rapporten kan ikke redigeres. Du kan se innholdet,
                      men ikke lagre endringer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
                     <div className="text-sm text-gray-600">{config.description}</div>

           {/* Show loading state while report data is being prepared */}
           {isDialogLoading ? (
             <div className="flex items-center justify-center py-8">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
               <span className="ml-3 text-gray-600">Laster rapportdata...</span>
             </div>
           ) : (
             <Formik
               enableReinitialize={true}
               initialValues={getInitialValues()}
               validationSchema={config.validation}
                onSubmit={async (values, helpers) => {
                  
                  if (!canEdit) {
                    toast.error("Du kan ikke redigere denne rapporten");
                    return;
                  }

                  // Prevent multiple submissions using simple boolean
                  if (isSubmitting) {
                    console.log("Already submitting, ignoring duplicate submission");
                    return;
                  }

                  // Set both boolean and state immediately
                  isSubmitting = true;
                  setIsLoading(true);

                  dispatchWithToastPromise(
                    dispatch(updateReportAsync(values)),
                    (updatedReport) => {
                      // Create a merged report object with the updated values
                      const mergedReport = {
                        ...report,
                        ...updatedReport,
                        // Ensure specific fields are properly merged
                        variables:
                          values.variables || updatedReport?.variables || report?.variables,
                        items: values.items || updatedReport?.items || report?.items,
                        checklist:
                          values.checklist || updatedReport?.checklist || report?.checklist,
                        report:
                          values.report !== undefined
                            ? values.report
                            : updatedReport?.report || report?.report,
                        internal_report:
                          values.internal_report !== undefined
                            ? values.internal_report
                            : updatedReport?.internal_report || report?.internal_report,
                        operations: updatedReport?.operations || report?.operations,
                        updated_at: updatedReport?.updated_at || report?.updated_at,
                      };

                      // Pass the merged report data directly to avoid server refresh
                      onRefresh(mergedReport);
                      onClose();
                    }
                  ).then((updatedReport) => {
                    // Reset loading state on success
                    isSubmitting = false;
                    setIsLoading(false);
                    
                    helpers.setStatus({ success: true });
                    console.log("Submit completed successfully");
                  }).catch((err) => {
                    console.error("Submit error:", err);
                    helpers.setStatus({ success: false });
                    helpers.setErrors({ submit: err.message });
										toast.error(err.message);
                    
                    // Reset loading state on error
                    isSubmitting = false;
                    setIsLoading(false);
                  });
                }}
               validateOnChange={false}
               validateOnBlur={true}
               validateOnMount={false}
             >
                         {(formik) => {
               return (
                <Form>
                  <div className="space-y-6">
                    {renderSectionContent(formik)}



                     {/* Variables: manual save. Items & report: direct / auto-save */}
                     {section !== "items" && section !== "report" && (
                       <div
                         className={`flex ${
                           canEdit ? "justify-end space-x-3" : "justify-center"
                         } pt-6 border-t`}
                       >
                        <PMButton
                          type="button"
                          variant="secondary"
                          onClick={() => void closeWithFlush()}
                          disabled={isLoading}
                          className={`${
                            !canEdit ? "w-full" : ""
                          } bg-gray-500 hover:bg-gray-600 text-white border-gray-500 hover:border-gray-600`}
                        >
                          Avbryt
                        </PMButton>
                        {canEdit && (
                          <PMButton
                            type="submit"
                            disabled={isLoading}
                            loading={isLoading}
                          >
                            Lagre
                          </PMButton>
                        )}
                      </div>
                     )}
                     {(section === "items" || section === "report") && (
                       <div className="flex justify-end pt-6 border-t">
                         <PMButton
                           type="button"
                           variant="secondary"
                           onClick={() => void closeWithFlush()}
                           disabled={
                             section === "report" && reportSaveUi === "saving"
                           }
                           loading={
                             section === "report" && reportSaveUi === "saving"
                           }
                           className="bg-gray-500 hover:bg-gray-600 text-white border-gray-500 hover:border-gray-600"
                         >
                           {section === "report" && reportSaveUi === "saving"
                             ? t("report.sectionDialog.saving")
                             : t("report.sectionDialog.close")}
                         </PMButton>
                       </div>
                     )}
                  </div>
                </Form>
                             );
             }}
             </Formik>
           )}
        </div>
      </PMDialog>

      {open && section === "report" && (
        <SaveBar
          isSaving={reportSaveUi === "saving"}
          isSuccess={reportSaveUi === "saved"}
          isError={reportSaveUi === "error"}
          errorMessage={t("report.sectionDialog.autoSaveError")}
          successMessage={t("report.sectionDialog.saved")}
          className="!z-[2100]"
        />
      )}

      <ReportChecklistDialog
        open={checklistDialogOpen}
        onClose={() => {
          setChecklistDialogOpen(false);
          // Close the parent dialog when checklist dialog closes
          if (section === "checklist") {
            onClose();
          }
        }}
        report={report}
        onRefresh={onRefresh}
      />


      {/* Validation Dialog */}
      <Transition.Root show={showValidationDialog} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowValidationDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg
                        className="h-6 w-6 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Kan ikke formatere rapport
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">{validationMessage}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                      onClick={() => setShowValidationDialog(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </Fragment>
  );
};

// Export component directly without React.memo to ensure re-renders when report changes
export const ReportSectionDialog = ReportSectionDialogComponent;
